import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import type { RootState } from "../../../store/store";
import { isAdminFromToken } from "../../../utils/authToken";

import {
  ImportDryRun,
  ImportError,
  ImportRejectedError,
  RgbStore,
  downloadRgbImportTemplateApi,
  getRgbStoresApi,
  importRgbMovementsApi,
  undoRgbImportApi,
} from "../api/rgbApi";
import { RGB_HOME_PAGE } from "../routes";
import { formatInt, todayIso } from "../rgbFormat";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Rgb.css";

type LastImport = { batchId: string; criados: number };

/** Mesma chave da tela inicial do RGB: a loja escolhida lá já vem aqui. */
const STORE_KEY = "rgb.lastStoreId";
const readLastStore = (): string | null => {
  try {
    return window.localStorage.getItem(STORE_KEY);
  } catch {
    return null;
  }
};

/**
 * Importação da planilha de contagem da loja (único formato aceito) em três
 * passos: loja e data (o arquivo não traz as duas), validar (dry-run — nada é
 * gravado) e confirmar. O backend é tudo ou nada: com qualquer erro na
 * planilha, a confirmação não grava nenhuma linha.
 */
export default function RgbImportPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(RGB_HOME_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);

  const inputRef = useRef<HTMLInputElement>(null);
  const [stores, setStores] = useState<RgbStore[]>([]);
  const [storeId, setStoreId] = useState("");
  const [data, setData] = useState(todayIso());
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState<ImportDryRun | null>(null);
  const [rejected, setRejected] = useState<ImportError[] | null>(null);
  const [busy, setBusy] = useState<"template" | "validate" | "confirm" | "undo" | null>(null);
  const [lastImport, setLastImport] = useState<LastImport | null>(null);
  const [confirmUndo, setConfirmUndo] = useState(false);

  const ctx = useMemo(() => ({ storeId: storeId || undefined, data: data || undefined }), [storeId, data]);
  const ready = Boolean(storeId && data);
  const errors = rejected ?? dryRun?.erros ?? [];
  const canConfirm = Boolean(file && dryRun && dryRun.erros.length === 0 && dryRun.validas > 0 && !rejected);

  useEffect(() => {
    getRgbStoresApi()
      .then((list) => {
        setStores(list);
        const remembered = readLastStore();
        const initial = list.find((s) => s.id === remembered) ?? (list.length === 1 ? list[0] : null);
        if (initial) setStoreId(initial.id);
      })
      .catch((error) => toast.error(error instanceof Error ? error.message : "Erro ao carregar as lojas."));
  }, []);

  const resetFile = () => {
    setFile(null);
    setDryRun(null);
    setRejected(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      await downloadRgbImportTemplateApi();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao baixar o modelo.");
    } finally {
      setBusy(null);
    }
  };

  const validate = async (f: File) => {
    if (!token) return;
    setBusy("validate");
    setDryRun(null);
    setRejected(null);
    try {
      setDryRun(await importRgbMovementsApi(token, f, false, ctx));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao validar a planilha.");
    } finally {
      setBusy(null);
    }
  };

  // Trocar loja ou data muda o que seria gravado: valida de novo o arquivo escolhido.
  useEffect(() => {
    if (file && ready) validate(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  const confirm = async () => {
    if (!token || !file) return;
    setBusy("confirm");
    try {
      const result = await importRgbMovementsApi(token, file, true, ctx);
      toast.success(`${result.criados} apontamento(s) importado(s).`);
      setLastImport({ batchId: result.importBatchId, criados: result.criados });
      resetFile();
    } catch (error) {
      // A planilha pode ter mudado entre a validação e a confirmação.
      if (error instanceof ImportRejectedError) setRejected(error.erros);
      toast.error(error instanceof Error ? error.message : "Erro ao importar.");
    } finally {
      setBusy(null);
    }
  };

  const undo = async () => {
    if (!lastImport) return;
    setBusy("undo");
    try {
      const { removidos } = await undoRgbImportApi(lastImport.batchId);
      toast.success(`${removidos} apontamento(s) removido(s).`);
      setLastImport(null);
      setConfirmUndo(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao desfazer.");
    } finally {
      setBusy(null);
    }
  };

  if (!token) return null;

  return (
    <div className="rgbLayout">
      <main className="rgbMain">
        <div className="rgbContainer">
          <EcoPageHeader
            crumbs={[{ label: "Vasilhame RGB", onClick: () => navigate(RGB_HOME_PAGE()) }, { label: "Importar planilha" }]}
            title="Importar planilha de vasilhame"
            subtitle="Vários apontamentos de uma vez. Nada é gravado até a confirmação, e com qualquer erro, nada é gravado."
            onBack={goBack}
            backLabel="Voltar para o RGB"
          />

          {lastImport ? (
            <div className="rgbNotice" style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              <span>
                Última importação: <strong>{lastImport.criados}</strong> apontamento(s), lote{" "}
                <code>{lastImport.batchId.slice(0, 8)}</code>.
              </span>
              {isAdmin ? (
                <button
                  className="rgbBtn rgbBtn--danger rgbBtn--small"
                  disabled={busy !== null}
                  onClick={() => setConfirmUndo(true)}
                >
                  Desfazer
                </button>
              ) : (
                <span className="rgbMuted">Para desfazer, peça a um administrador.</span>
              )}
            </div>
          ) : null}

          <div className="rgbSteps">
            <section className="rgbSection" style={{ marginBottom: 0 }}>
              <h2 className="rgbSection__title">1. Escolha a loja e a data</h2>
              <p className="rgbMuted" style={{ marginTop: 0 }}>
                A planilha de contagem não traz loja nem data: o refugo é lançado nesta loja e nesta data.
              </p>
              <div className="rgbControls">
                <div className="rgbFormGroup">
                  <label htmlFor="rgbImportStore">Loja</label>
                  <select
                    id="rgbImportStore"
                    className="rgbSelect"
                    value={storeId}
                    disabled={busy !== null}
                    onChange={(e) => setStoreId(e.target.value)}
                  >
                    <option value="">Selecione a loja...</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nomeFantasia}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rgbFormGroup">
                  <label htmlFor="rgbImportData">Data</label>
                  <input
                    id="rgbImportData"
                    className="rgbInput"
                    type="date"
                    max={todayIso()}
                    value={data}
                    disabled={busy !== null}
                    onChange={(e) => setData(e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className="rgbSection" style={{ marginBottom: 0 }}>
              <h2 className="rgbSection__title">2. Envie a planilha para validar</h2>
              <p className="rgbMuted" style={{ marginTop: 0 }}>
                Planilha de contagem com as colunas Código, Descrição, Contagem, Segunda contagem e Status. Vale a segunda
                contagem quando preenchida (senão, a primeira), o item é casado pelo código do vasilhame, item com
                quantidade 0 fica de fora e o motivo do refugo é “Outro”.{" "}
                <button
                  type="button"
                  className="rgbBtn rgbBtn--secondary rgbBtn--small"
                  disabled={busy !== null}
                  onClick={downloadTemplate}
                >
                  {busy === "template" ? "Baixando..." : "Baixar modelo"}
                </button>
              </p>
              <input
                ref={inputRef}
                className="rgbFileInput"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={busy !== null || !ready}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  if (f) validate(f);
                  else resetFile();
                }}
              />
              {!ready ? <p className="rgbMuted">Escolha a loja e a data antes de enviar.</p> : null}
              {busy === "validate" ? <p className="rgbMuted">Validando...</p> : null}

              {dryRun ? (
                <div style={{ marginTop: 16 }}>
                  <div className="rgbImportTotals">
                    <div className="rgbImportTotal">
                      <div className="rgbImportTotal__value">{formatInt(dryRun.totalLinhas)}</div>
                      <div className="rgbImportTotal__label">Linhas lidas</div>
                    </div>
                    <div className="rgbImportTotal rgbImportTotal--ok">
                      <div className="rgbImportTotal__value">{formatInt(dryRun.validas)}</div>
                      <div className="rgbImportTotal__label">Válidas</div>
                    </div>
                    {dryRun.ignoradas > 0 ? (
                      <div className="rgbImportTotal">
                        <div className="rgbImportTotal__value">{formatInt(dryRun.ignoradas)}</div>
                        <div className="rgbImportTotal__label">Com quantidade 0</div>
                      </div>
                    ) : null}
                    <div className="rgbImportTotal rgbImportTotal--problem">
                      <div className="rgbImportTotal__value">
                        {formatInt(new Set(errors.map((e) => e.linha)).size)}
                      </div>
                      <div className="rgbImportTotal__label">Com erro</div>
                    </div>
                  </div>
                </div>
              ) : null}

              {errors.length > 0 ? (
                <>
                  <div className="rgbNotice rgbNotice--warning">
                    {rejected
                      ? "A planilha mudou ou tem erros — nada foi importado. Corrija as linhas abaixo e envie de novo."
                      : "Corrija as linhas abaixo na planilha e envie de novo. Enquanto houver erro, a importação fica bloqueada."}
                  </div>
                  <div className="rgbImportRows">
                    <table className="rgbTable">
                      <thead>
                        <tr>
                          <th style={{ width: 70 }}>Linha</th>
                          <th style={{ width: 140 }}>Coluna</th>
                          <th>Erro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {errors.map((e, i) => (
                          <tr key={`${e.linha}-${i}`}>
                            <td>{e.linha}</td>
                            <td>{e.coluna ?? "—"}</td>
                            <td>{e.erro}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}

              {dryRun && dryRun.preview.length > 0 ? (
                <>
                  <p className="rgbMuted" style={{ margin: "0 0 8px 0" }}>
                    Prévia das primeiras linhas válidas{dryRun.validas > dryRun.preview.length ? ` (${dryRun.preview.length} de ${dryRun.validas})` : ""}:
                  </p>
                  <div className="rgbImportRows">
                    <table className="rgbTable">
                      <thead>
                        <tr>
                          <th>Linha</th>
                          <th>Vasilhame</th>
                          <th className="is-num">Qtd</th>
                          <th>Contagens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dryRun.preview.map((r) => (
                          <tr key={r.linha}>
                            <td>{r.linha}</td>
                            <td>{r.vasilhame}</td>
                            <td className="is-num">{formatInt(r.quantidade)}</td>
                            <td>{r.observacao ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </section>

            <section className="rgbSection" style={{ marginBottom: 0 }}>
              <h2 className="rgbSection__title">3. Confirme</h2>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button className="rgbBtn rgbBtn--primary" disabled={!canConfirm || busy !== null} onClick={confirm}>
                  {busy === "confirm"
                    ? "Importando..."
                    : dryRun && canConfirm
                      ? `Importar ${formatInt(dryRun.validas)} apontamento(s)`
                      : "Importar"}
                </button>
                {file ? (
                  <button className="rgbBtn rgbBtn--secondary" disabled={busy !== null} onClick={resetFile}>
                    Trocar arquivo
                  </button>
                ) : null}
              </div>
              {!canConfirm && dryRun && dryRun.erros.length === 0 && dryRun.validas === 0 ? (
                <p className="rgbMuted">A planilha não tem linhas para importar.</p>
              ) : null}
            </section>
          </div>
        </div>
      </main>

      <EcoConfirmDialog
        open={confirmUndo}
        title="Desfazer a importação?"
        message={`Os ${lastImport?.criados ?? 0} apontamento(s) deste lote serão removidos.`}
        confirmLabel="Desfazer"
        tone="danger"
        busy={busy === "undo"}
        onConfirm={undo}
        onCancel={() => setConfirmUndo(false)}
      />
    </div>
  );
}
